import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import { InventoryLot } from "@/models/InventoryLot";
import { itemRouteBuilder, NamedRts } from "@/router";

import ListPageHeader from "@/components/ListPageHeader/ListPageHeader.vue";
import { IlsctlLineItem } from "@/models/IlsctlLineItem";

import { debounce } from "@/utilities/debounce";
import { genericSort, sortIcon } from "@/components/helpers";

import {
  arrowUpHandler,
  arrowDownHandler,
  filterOnInputHandler,
  filterItems,
} from "@/utilities/pickListHelpers";
import { currencyFormatter } from "@/utilities/display-helpers";

export default defineComponent({
  name: "inventoryLotIlsctlLineItems",
  props: {
    useMode: {
      type: String,
      required: false,
    },
  },
  components: {
    ListPageHeader,
  },

  setup(props, ctx) {
    const $store = ctx.root.$store;

    const firstInput = ref<null | HTMLInputElement>(null);

    const showMenu = ref(false);

    const apiInventoryLot = computed(() => {
      return $store.getters.inventoryLot as InventoryLot;
    });

    const apiIlsctlLineItems = computed(() => {
      return apiInventoryLot.value.ilsctl_line_items as IlsctlLineItem[];
    });

    const scShowRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.sellContractIlsctlLineItems, id);
    };

    const tlShowRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.buildTruckLoad, id);
    };

    const tlReportRouteBuilder = (items: IlsctlLineItem[]) => {
      const ids = items.map(item => {
        return item.truck_load_id;
      });

      return {
        name: NamedRts.truckLoadReports,
        query: {
          truckLoadIds: [...ids],
        },
      };
    };

    const ilsctlLineItems = ref<IlsctlLineItem[]>([]);

    const filterableKeys: Array<keyof IlsctlLineItem> = [
      "completed_date",
      "inventory_lot_label",
      "truck_load_label",
      "il_quantity",
    ];

    const filter = ref("");

    const clearFilter = () => {
      filterOnInputHandler(
        apiIlsctlLineItems,
        ilsctlLineItems,

        "",
        filter,
        debouncedFilteredIlsctlLineItems,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterLineItems = (filter: string, wo: IlsctlLineItem[]) => {
      return filterItems(filter, filterableKeys, wo);
    };

    const debouncedFilteredIlsctlLineItems = debounce(filterLineItems, 500);

    const sortKey = ref<keyof IlsctlLineItem>("truck_load_label");
    const sortAsc = ref(true);

    const localSortIcon = (lSortKey: keyof IlsctlLineItem) => {
      return sortIcon(sortAsc, sortKey, lSortKey);
    };

    const updateSort = (lSortKey: keyof IlsctlLineItem) => {
      ilsctlLineItems.value = genericSort(
        [...ilsctlLineItems.value],
        sortAsc,
        sortKey,
        lSortKey,
      );
    };

    onMounted(() => {
      ilsctlLineItems.value = [...apiIlsctlLineItems.value];
      ctx.root.$nextTick(() => {
        if (firstInput.value) {
          firstInput.value.focus();
        }
      });
    });

    // this is for when the work orders are used as a picker
    // they make the arrow keys work correctly
    const selectedIndex = ref(-1);

    const onArrowDown = (evt: any) => {
      evt.preventDefault();
      arrowDownHandler(selectedIndex, ilsctlLineItems.value.length);
    };

    const onArrowUp = (evt: any) => {
      evt.preventDefault();
      arrowUpHandler(selectedIndex);
    };

    const filterOnInput = ({ target }: { target: HTMLInputElement }) => {
      filterOnInputHandler(
        apiIlsctlLineItems,
        ilsctlLineItems,

        target.value,
        filter,
        debouncedFilteredIlsctlLineItems,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterOnEnter = (evt: any) => {
      evt.preventDefault();
      let tmpResult = ilsctlLineItems.value[selectedIndex.value];

      if (!tmpResult) {
        tmpResult = ilsctlLineItems.value[0];
      }

      if (props.useMode === "pickerMode") {
        sendIlsctlLineItem(tmpResult);
      } else {
        ctx.root.$router.push(scShowRouteBuilder(tmpResult.sell_contract_id));
      }
    };

    const sendIlsctlLineItem = (il: IlsctlLineItem) => {
      ctx.emit("sendIlsctlLineItem", il);
    };

    return {
      apiInventoryLot,
      scShowRouteBuilder,
      tlShowRouteBuilder,

      ilsctlLineItems,

      filter,
      filterOnEnter,
      filterOnInput,
      clearFilter,

      showMenu,

      localSortIcon,
      updateSort,

      onArrowDown,
      onArrowUp,
      selectedIndex,
      sendIlsctlLineItem,

      firstInput,

      currencyFormatter,

      tlReportRouteBuilder,
    };
  },
});
